import { PlusCircleIcon, TrashIcon } from "@heroicons/react/outline";
import Button from "components/button";
import Input from "components/input";
import { Label } from "components/label";
import Select from "components/select";
import { ApplyToLevel, DiscountType, ICreateCouponForm } from "interfaces";
import { isEmpty } from "lodash";
import {
  Controller,
  UseFieldArrayReturn,
  useFormContext,
} from "react-hook-form";
import { DISCOUNT_TYPES } from "utills/constants";

interface QuantityDiscountRulesProps {
  quantityDiscountRuleArrController: UseFieldArrayReturn<
    ICreateCouponForm,
    "quantity_discount_rules",
    "id"
  >;
  shouldShowContentByEventCurrency: (currency: string) => boolean;
  discountTypeWatch: DiscountType;
}

export const QuantityDiscountRules = ({
  quantityDiscountRuleArrController,
  shouldShowContentByEventCurrency,
  discountTypeWatch,
}: QuantityDiscountRulesProps) => {
  const {
    control,
    register,
    watch,
    resetField,
    trigger,
    formState: { errors },
  } = useFormContext<ICreateCouponForm>(); // retrieve all hook methods

  const {
    fields: ruleFields,
    append: appendRule,
    remove: removeRule,
    replace: replaceRuleFields,
  } = quantityDiscountRuleArrController;

  const handleAddRule = () => {
    appendRule({
      qty: 1,
      apply_to_level: ApplyToLevel.TOTAL_ORDER,
      discount_type: DiscountType.PERCENT,
      discount_value: null,
      discount_value_usd: null,
      max_discount_value: null,
      max_discount_value_usd: null,
    });
    trigger("quantity_discount_rules");
  };

  const handleRemoveRule = (index: number) => {
    removeRule(index);
  };

  const clearDiscountTypeRelatedFields = (index: number) => {
    resetField(`quantity_discount_rules.${index}.discount_value`);
    resetField(`quantity_discount_rules.${index}.discount_value_usd`);

    resetField(`quantity_discount_rules.${index}.max_discount_value`);
    resetField(`quantity_discount_rules.${index}.max_discount_value_usd`);
  };

  const discountTypesArr = DISCOUNT_TYPES.filter(
    (type) =>
      type.value === DiscountType.PERCENT ||
      type.value === DiscountType.FIX_PRICE
  );

  return (
    <div>
      <Label
        required
        id="note"
        name={
          discountTypeWatch === DiscountType.GROUP_DISCOUNT
            ? "Group Discount Condition"
            : "Quantity Discount Condition"
        }
        hint={
          discountTypeWatch === DiscountType.GROUP_DISCOUNT
            ? "Group Discount will be applied to Total Order"
            : "Quantity Discount will be applied to Total Order"
        }
      />
      <Button
        type="button"
        className="w-fit px-2.5 py-1.5 text-sm bg-white text-brand-500 border border-solid border-brand-500 hover:bg-gray-50 active:bg-gray-100"
        onClick={handleAddRule}
      >
        <PlusCircleIcon className="w-5 h-5 mr-1.5" />{" "}
        <span className="shrink-0 text-sm">Add Condition</span>
      </Button>
      {!isEmpty(ruleFields) && (
        <div className="bg-white flex flex-col gap-y-4 mt-4">
          {ruleFields.map((field, index) => (
            <div key={field.id} className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <Label
                  required
                  id={`quantity_discount_rules.${index}.qty`}
                  name="Quantity"
                />
                <Input
                  id={`quantity_discount_rules.${index}.qty`}
                  type="number"
                  className="bg-white"
                  error={errors["quantity_discount_rules"]?.[index]?.qty}
                  placeholder="Quantity"
                  {...register(`quantity_discount_rules.${index}.qty`, {
                    valueAsNumber: true,
                  })}
                />
                {errors["quantity_discount_rules"]?.[index]?.qty ? (
                  <p className="error-text">
                    <span className="font-medium">
                      {errors["quantity_discount_rules"]?.[index]?.qty?.message}
                    </span>
                  </p>
                ) : null}
              </div>
              <div className="flex-1">
                <Label
                  required
                  id={`quantity_discount_rules.${index}.discount_type`}
                  name="Discount Type"
                />
                <Controller
                  control={control}
                  name={`quantity_discount_rules.${index}.discount_type`}
                  render={({ field: { onChange, value = "", ref } }) => {
                    return (
                      <Select
                        key={`quantity_discount_rules.${index}.discount_type`}
                        inputRef={ref}
                        options={discountTypesArr}
                        id={`quantity_discount_rules.${index}.discount_type`}
                        placeholder="Select discount type"
                        defaultValue={
                          discountTypesArr.find(
                            (selected: { label: string; value: any }) =>
                              selected.value === value
                          ) ?? ""
                        }
                        value={discountTypesArr.find(
                          (selected: { label: string; value: any }) =>
                            selected.value === value
                        )}
                        onChange={(selected: { label: string; value: any }) => {
                          onChange(selected?.value);
                          clearDiscountTypeRelatedFields(index);
                        }}
                        error={
                          errors["quantity_discount_rules"]?.[index]
                            ?.discount_type
                        }
                      />
                    );
                  }}
                />
                {errors["quantity_discount_rules"]?.[index]?.discount_type ? (
                  <p className="error-text">
                    <span className="font-medium">
                      {
                        errors["quantity_discount_rules"]?.[index]
                          ?.discount_type?.message
                      }
                    </span>
                  </p>
                ) : null}
              </div>
              {watch("quantity_discount_rules")?.[index]?.discount_type ===
              DiscountType.PERCENT ? (
                <>
                  <div className="flex-1">
                    <Label required id="discount_value" name="Discount Value" />
                    <div className="relative">
                      <Input
                        id="discount_value"
                        type="number"
                        error={
                          errors["quantity_discount_rules"]?.[index]
                            ?.discount_value
                        }
                        placeholder="Discount Value"
                        {...register(
                          `quantity_discount_rules.${index}.discount_value`
                        )}
                        min={0}
                      />
                      <span className="w-3 bg-white absolute top-1/2 right-3 -translate-y-1/2">
                        %
                      </span>
                    </div>
                    {errors["quantity_discount_rules"]?.[index]
                      ?.discount_value ? (
                      <p className="error-text">
                        <span className="font-medium">
                          {
                            errors["quantity_discount_rules"]?.[index]
                              ?.discount_value?.message
                          }
                        </span>
                      </p>
                    ) : null}
                  </div>
                  {shouldShowContentByEventCurrency("vnd") ? (
                    <div className="flex-1">
                      <Label
                        id={`quantity_discount_rules.${index}.max_discount_value`}
                        name="Max discount amount (đ)"
                      />
                      <div className="relative">
                        <Input
                          id={`quantity_discount_rules.${index}.max_discount_value`}
                          type="number"
                          error={
                            errors["quantity_discount_rules"]?.[index]
                              ?.max_discount_value
                          }
                          placeholder="Max discount amount"
                          {...register(
                            `quantity_discount_rules.${index}.max_discount_value`
                          )}
                          min={0}
                        />
                        <span className="w-3 bg-white absolute top-1/2 right-3 -translate-y-1/2">
                          đ
                        </span>
                      </div>
                      {errors["quantity_discount_rules"]?.[index]
                        ?.max_discount_value ? (
                        <p className="error-text">
                          <span className="font-medium">
                            {
                              errors["quantity_discount_rules"]?.[index]
                                ?.max_discount_value?.message
                            }
                          </span>
                        </p>
                      ) : null}
                    </div>
                  ) : (
                    <></>
                  )}
                  {shouldShowContentByEventCurrency("usd") ? (
                    <div className="flex-1">
                      <Label
                        id={`quantity_discount_rules.${index}.max_discount_value_usd`}
                        name="Max discount amount ($)"
                      />
                      <div className="relative">
                        <Input
                          id={`quantity_discount_rules.${index}.max_discount_value_usd`}
                          type="number"
                          error={
                            errors["quantity_discount_rules"]?.[index]
                              ?.max_discount_value_usd
                          }
                          placeholder="Max discount amount"
                          {...register(
                            `quantity_discount_rules.${index}.max_discount_value_usd`
                          )}
                          min={0}
                        />
                        <span className="w-3 bg-white absolute top-1/2 right-3 -translate-y-1/2">
                          $
                        </span>
                      </div>
                      {errors["quantity_discount_rules"]?.[index]
                        ?.max_discount_value_usd ? (
                        <p className="error-text">
                          <span className="font-medium">
                            {
                              errors["quantity_discount_rules"]?.[index]
                                ?.max_discount_value_usd?.message
                            }
                          </span>
                        </p>
                      ) : null}
                    </div>
                  ) : (
                    <></>
                  )}
                </>
              ) : (
                <></>
              )}

              {watch("quantity_discount_rules")?.[index]?.discount_type ===
              DiscountType.FIX_PRICE ? (
                <>
                  {shouldShowContentByEventCurrency("vnd") ? (
                    <div className="flex-1">
                      <Label
                        required
                        id={`quantity_discount_rules.${index}.discount_value`}
                        name="Discount Value (đ)"
                      />
                      <div className="relative">
                        <Input
                          id={`quantity_discount_rules.${index}.discount_value`}
                          type="number"
                          error={
                            errors["quantity_discount_rules"]?.[index]
                              ?.discount_value
                          }
                          placeholder="Discount Value"
                          {...register(
                            `quantity_discount_rules.${index}.discount_value`
                          )}
                          min={0}
                        />
                        <span className="w-3 bg-white absolute top-1/2 right-3 -translate-y-1/2">
                          đ
                        </span>
                      </div>
                      {errors["quantity_discount_rules"]?.[index]
                        ?.discount_value ? (
                        <p className="error-text">
                          <span className="font-medium">
                            {
                              errors["quantity_discount_rules"]?.[index]
                                ?.discount_value?.message
                            }
                          </span>
                        </p>
                      ) : null}
                    </div>
                  ) : (
                    <></>
                  )}
                  {shouldShowContentByEventCurrency("usd") ? (
                    <div className="flex-1">
                      <Label
                        required
                        id={`quantity_discount_rules.${index}.discount_value_usd`}
                        name="Discount Value ($)"
                      />
                      <div className="relative">
                        <Input
                          id={`quantity_discount_rules.${index}.discount_value_usd`}
                          type="number"
                          error={
                            errors["quantity_discount_rules"]?.[index]
                              ?.discount_value_usd
                          }
                          placeholder="Discount Value"
                          {...register(
                            `quantity_discount_rules.${index}.discount_value_usd`
                          )}
                          min={0}
                        />
                        <span className="w-3 bg-white absolute top-1/2 right-3 -translate-y-1/2">
                          $
                        </span>
                      </div>
                      {errors["quantity_discount_rules"]?.[index]
                        ?.discount_value_usd ? (
                        <p className="error-text">
                          <span className="font-medium">
                            {
                              errors["quantity_discount_rules"]?.[index]
                                ?.discount_value_usd?.message
                            }
                          </span>
                        </p>
                      ) : null}
                    </div>
                  ) : (
                    <></>
                  )}
                </>
              ) : (
                <></>
              )}
              {ruleFields.length > 1 && (
                <div className="shrink-0">
                  <button
                    type="button"
                    onClick={() => handleRemoveRule(index)}
                    className="text-red-600 hover:bg-red-50 rounded sm:mt-11"
                  >
                    <TrashIcon className="size-4" />
                  </button>
                </div>
              )}
            </div>
          ))}
          {errors["quantity_discount_rules"]?.root ? (
            <p className="error-text">
              <span className="font-medium">
                {errors["quantity_discount_rules"]?.root?.message}
              </span>
            </p>
          ) : null}
        </div>
      )}
    </div>
  );
};
